import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import AppLayout from "@/components/layout/AppLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminProtectedRoute from "@/components/layout/AdminProtectedRoute";
import ReceptionLayout from "@/components/layout/ReceptionLayout";
import ScrollToTop from "@/components/common/ScrollToTop";

// Zona pública
import HomePublica from "@/screens/public/HomePublica";
import Login from "@/screens/public/Login";
import ActivarCuenta from "@/screens/public/ActivarCuenta";

// Zona privada (socio autenticado)
import Inicio from "@/screens/app/Inicio";
import MisBoletos from "@/screens/app/MisBoletos";
import MiPerfil from "@/screens/app/MiPerfil";
import Notificaciones from "@/screens/app/Notificaciones";
import EventoDetalle from "@/screens/app/EventoDetalle";
import SmartQueue from "@/screens/app/SmartQueue";
import SeleccionMesa from "@/screens/app/SeleccionMesa";
import SeleccionAccesoGeneral from "@/screens/app/SeleccionAccesoGeneral";
import ResumenCompra from "@/screens/app/ResumenCompra";
import Pago from "@/screens/app/Pago";
import CompraConfirmada from "@/screens/app/CompraConfirmada";
import AsignarAsistentes from "@/screens/app/AsignarAsistentes";
import DetalleEventoCompras from "@/screens/app/DetalleEventoCompras";
import BoletoDigital from "@/screens/app/BoletoDigital";

// Portal administrativo
import AdminLogin from "@/screens/admin/AdminLogin";
import AdminDashboard from "@/screens/admin/Dashboard";
import AdminEventosListado from "@/screens/admin/eventos/Listado";
import EventoWizard from "@/screens/admin/eventos/EventoWizard";
import AdminCambioMesa from "@/screens/admin/eventos/CambioMesa";
import PlantillasMesasListado from "@/screens/admin/plantillas/PlantillasMesasListado";
import PlantillaMesasEditor from "@/screens/admin/plantillas/PlantillaMesasEditor";
import AdminUsuarios from "@/screens/admin/Usuarios";
import AdminNotificaciones from "@/screens/admin/Notificaciones";
import AdminBitacora from "@/screens/admin/Bitacora";
import AdminBloqueos from "@/screens/admin/Bloqueos";
import AdminConfiguracion from "@/screens/admin/Configuracion";
import AdminReportes from "@/screens/admin/Reportes";

// Portal de recepción (Módulo 4)
import RecepcionSeleccionEvento from "@/screens/reception/SeleccionEvento";
import RecepcionEscaner from "@/screens/reception/Escaner";
import ReceptionLogin from "@/screens/reception/ReceptionLogin";

// Sprint 47 · Manejador global de sesión terminada por el backend
import { useEffect } from "react";
import { toast } from "sonner";
import { clearAdminSessionLocal } from "@/data/adminAuthStore";
import { clearSocioSessionLocal } from "@/data/socioAuthStore";
import { clearReceptionSessionLocal } from "@/data/receptionAuthStore";

function SessionTerminationListener() {
  useEffect(() => {
    const handler = (e) => {
      const reason = e?.detail?.reason || "";
      const msg =
        reason === "INACTIVIDAD"
          ? "Tu sesión se cerró por inactividad."
          : reason === "NUEVA_SESION"
            ? "Tu sesión fue cerrada porque se inició otra desde un dispositivo distinto."
            : e?.detail?.message || "Sesión finalizada.";
      // Limpia todos los snapshots locales por si el usuario tenía
      // varios portales abiertos.
      try { clearAdminSessionLocal(); } catch { /* ignore */ }
      try { clearSocioSessionLocal(); } catch { /* ignore */ }
      try { clearReceptionSessionLocal(); } catch { /* ignore */ }
      toast.warning(msg, { duration: 5000 });
      const path = window.location.pathname;
      const flash = reason === "INACTIVIDAD" ? "inactividad" : "nueva-sesion";
      const target =
        path.startsWith("/admin")
          ? `/admin/login?aviso=${flash}`
          : path.startsWith("/recepcion")
            ? `/recepcion/login?aviso=${flash}`
            : path.startsWith("/app")
              ? `/login?aviso=${flash}`
              : window.location.pathname;
      if (target !== window.location.pathname) {
        window.location.replace(target);
      }
    };
    window.addEventListener("ct:session-terminated", handler);
    return () => window.removeEventListener("ct:session-terminated", handler);
  }, []);
  return null;
}

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <SessionTerminationListener />
        <ScrollToTop />
        <Routes>
          {/* Zona pública · sin barra inferior */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePublica />} />
            <Route path="/login" element={<Login />} />
            <Route path="/activar" element={<ActivarCuenta />} />
          </Route>

          {/* Zona privada del socio · con barra inferior */}
          <Route path="/app" element={<AppLayout />}>
            <Route index element={<Inicio />} />
            <Route path="mis-boletos" element={<MisBoletos />} />
            <Route path="mi-perfil" element={<MiPerfil />} />
            <Route path="notificaciones" element={<Notificaciones />} />
            <Route path="eventos/:id" element={<EventoDetalle />} />
            <Route path="eventos/:id/smart-queue" element={<SmartQueue />} />
            <Route path="eventos/:id/seleccion-mesa" element={<SeleccionMesa />} />
            <Route path="eventos/:id/acceso-general" element={<SeleccionAccesoGeneral />} />
            <Route path="eventos/:id/resumen" element={<ResumenCompra />} />
            <Route path="eventos/:id/pago" element={<Pago />} />
            <Route path="eventos/:id/compra-confirmada" element={<CompraConfirmada />} />
            <Route path="compra-confirmada/:compraId" element={<CompraConfirmada />} />
            <Route path="eventos/:eventoId/asignar" element={<AsignarAsistentes />} />
            <Route path="eventos/:eventoId/mis-compras" element={<DetalleEventoCompras />} />
            <Route path="asignar/:compraId" element={<AsignarAsistentes />} />
            <Route path="boletos/:boletoId" element={<BoletoDigital />} />
          </Route>

          {/* Portal de recepción · layout aislado */}
          <Route path="/recepcion/login" element={<ReceptionLogin />} />
          <Route path="/recepcion" element={<ReceptionLayout />}>
            <Route index element={<RecepcionSeleccionEvento />} />
            <Route path="eventos/:eventoId" element={<RecepcionEscaner />} />
          </Route>

          {/* Portal administrativo */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<AdminProtectedRoute />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="eventos" element={<AdminEventosListado />} />
              <Route path="eventos/nuevo" element={<EventoWizard modo="crear" />} />
              <Route
                path="eventos/:eventoId/editar"
                element={<EventoWizard modo="editar" />}
              />
              <Route
                path="eventos/:eventoId/cambio-mesa"
                element={<AdminCambioMesa />}
              />
              <Route path="plantillas-mesas" element={<PlantillasMesasListado />} />
              <Route path="plantillas-mesas/nueva" element={<PlantillaMesasEditor />} />
              <Route path="plantillas-mesas/:id" element={<PlantillaMesasEditor />} />
              <Route path="usuarios" element={<AdminUsuarios />} />
              <Route path="notificaciones" element={<AdminNotificaciones />} />
              <Route path="bitacora" element={<AdminBitacora />} />
              <Route path="bloqueos" element={<AdminBloqueos />} />
              <Route path="configuracion" element={<AdminConfiguracion />} />
              <Route path="reportes" element={<AdminReportes />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
